import * as React from 'react';
import { Input, type InputProps } from './input';
import { ValidationStatusIcon, type CheckStatus } from './validation-status';

/**
 * Validates a Swiss/Liechtenstein IBAN using ISO 7064 mod-97-10.
 * Returns true if the IBAN is structurally valid.
 */
function validateSwissIban(raw: string): boolean {
  if (raw.length !== 21) return false;
  if (!/^(CH|LI)\d{2}\d{5}[A-Z0-9]{12}$/.test(raw)) return false;

  // Move first 4 chars to end, convert letters to digits (A=10, B=11, ...)
  const rearranged = raw.slice(4) + raw.slice(0, 4);
  const digits = rearranged.replace(/[A-Z]/g, (ch) =>
    String(ch.charCodeAt(0) - 55),
  );

  // Mod-97 on the large number (process in chunks to avoid BigInt)
  let remainder = 0;
  for (const digit of digits) {
    remainder = (remainder * 10 + Number(digit)) % 97;
  }

  return remainder === 1;
}

/** Format a raw IBAN string into groups of 4 separated by spaces */
export function formatIban(raw: string): string {
  return raw.replace(/(.{4})(?=.)/g, '$1 ');
}

/** Strip all non-alphanumeric characters */
function stripIban(value: string): string {
  return value.replace(/[^A-Z0-9]/gi, '').toUpperCase();
}

const SWISS_IBAN_MAX_LENGTH = 21;

export interface IbanInputProps extends Omit<
  InputProps,
  'value' | 'onChange' | 'type' | 'maxLength' | 'endAdornment'
> {
  /** Raw IBAN value without spaces (e.g. "CH9300762011623852957") */
  value?: string;
  /** Called with the raw (unformatted) IBAN string */
  onChange?: (value: string) => void;
}

const IbanInput = React.forwardRef<HTMLInputElement, IbanInputProps>(
  ({ value: controlledValue, onChange, ...props }, ref) => {
    const [internalValue, setInternalValue] = React.useState('');
    const isControlled = controlledValue !== undefined;
    const rawValue = isControlled ? controlledValue : internalValue;

    const [status, setStatus] = React.useState<CheckStatus>({ type: 'idle' });

    React.useEffect(() => {
      if (rawValue.length === SWISS_IBAN_MAX_LENGTH) {
        if (validateSwissIban(rawValue)) {
          setStatus({ type: 'valid' });
        } else {
          setStatus({ type: 'invalid' });
        }
      } else {
        setStatus({ type: 'idle' });
      }
    }, [rawValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = stripIban(e.target.value).slice(0, SWISS_IBAN_MAX_LENGTH);

      if (!isControlled) setInternalValue(raw);
      onChange?.(raw);
    };

    const statusIcon =
      status.type !== 'idle' ? (
        <ValidationStatusIcon status={status} />
      ) : undefined;

    return (
      <Input
        ref={ref}
        value={formatIban(rawValue)}
        onChange={handleChange}
        placeholder="CH93 0076 2011 6238 5295 7"
        inputMode="text"
        autoComplete="off"
        spellCheck={false}
        aria-invalid={status.type === 'invalid' || undefined}
        endAdornment={statusIcon}
        {...props}
        className={props.className}
        style={{
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '0.05em',
          ...props.style,
        }}
      />
    );
  },
);
IbanInput.displayName = 'IbanInput';

export { IbanInput, validateSwissIban };
